import React, { useState } from 'react';
import { Box, Typography, Sheet, Button, CircularProgress, Alert } from '@mui/joy';
import { useNavigate, useParams } from 'react-router-dom';
import { useOrderStore } from '../hooks/useOrder';
import { sendOrderEmail, isSignedIn, signIn } from '../utils/graphService';

const WindowOrderConfirmation: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const { orders } = useOrderStore();
  const navigate = useNavigate();
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<{ success: boolean; message: string } | null>(null);

  // Find the order by ID
  const order = orders.find(o => o.id === orderId);

  if (!order) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography level="h4" mb={2}>Window Order Not Found</Typography>
        <Typography level="body-md" mb={3}>
          The window order you're looking for could not be found.
        </Typography>
        <Button variant="outlined" onClick={() => navigate('/window-designer')}>
          Return to Window Designer
        </Button>
      </Box>
    );
  }

  const handleSendEmail = async () => {
    try {
      setSending(true);
      setSendResult(null);

      // Check if user is signed in
      const userSignedIn = await isSignedIn();
      if (!userSignedIn) {
        // Prompt user to sign in
        const signInResult = await signIn();
        if (!signInResult) {
          throw new Error('Sign in failed or was cancelled');
        }
      }

      // Send the email
      if (order.supplier) {
        if (!order.supplier.email) {
          throw new Error('Supplier does not have an email address configured');
        }
        await sendOrderEmail(order, order.supplier.email, true);
        setSendResult({
          success: true,
          message: `Email sent successfully to ${order.supplier.supplier_name}`,
        });
      } else {
        throw new Error('No supplier assigned to this order');
      }
    } catch (error) {
      setSendResult({
        success: false,
        message: error instanceof Error ? error.message : 'Failed to send email',
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography level="h3" mb={2}>Window Order Confirmation</Typography>
      
      <Alert color="success" sx={{ mb: 3 }}>
        Your window order has been placed successfully! Your reference number is <strong>{order.orderNumber}</strong>
      </Alert>
      
      <Sheet sx={{ p: 3, mb: 3, borderRadius: 'md', boxShadow: 'sm' }}>
        <Typography level="h4" mb={2}>Order Details</Typography>
        
        <Typography level="title-md" mt={2}>Customer Information</Typography>
        <Box sx={{ pl: 2, mb: 2 }}>
          <Typography level="body-md">Name: {order.customerInfo.name}</Typography>
          {order.customerInfo.email && (
            <Typography level="body-md">Email: {order.customerInfo.email}</Typography>
          )}
          {order.customerInfo.phone && (
            <Typography level="body-md">Phone: {order.customerInfo.phone}</Typography>
          )}
          {order.customerInfo.customerReference && (
            <Typography level="body-md">Reference: {order.customerInfo.customerReference}</Typography>
          )}
        </Box>
        
        <Typography level="title-md" mt={2}>Order Summary</Typography>
        <Box sx={{ pl: 2, mb: 2 }}>
          <Typography level="body-md">Windows: {order.items.length}</Typography>
          <Typography level="body-md">Subtotal: £{order.subtotal.toFixed(2)}</Typography>
          <Typography level="body-md">VAT: £{order.vat.toFixed(2)}</Typography>
          <Typography level="body-md" fontWeight="bold">Total: £{order.total.toFixed(2)}</Typography>
        </Box>
        
        <Typography level="title-md" mt={2}>Supplier</Typography>
        <Box sx={{ pl: 2, mb: 2 }}>
          {order.supplier ? (
            <Typography level="body-md">{order.supplier.supplier_name}</Typography>
          ) : (
            <Typography level="body-md" color="warning">No supplier assigned</Typography>
          )}
        </Box>
      </Sheet>
      
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button variant="outlined" onClick={() => navigate('/window-designer')}>
          Return to Window Designer
        </Button>
        
        {order.supplier && (
          <Button 
            variant="solid" 
            color="primary" 
            onClick={handleSendEmail} 
            disabled={sending}
            startDecorator={sending ? <CircularProgress size="sm" /> : undefined}
          >
            {sending ? 'Sending...' : 'Send Order Email'}
          </Button>
        )}
      </Box>
      
      {sendResult && (
        <Alert color={sendResult.success ? 'success' : 'danger'} sx={{ mt: 2 }}>
          {sendResult.message}
        </Alert>
      )}
    </Box>
  );
};

export default WindowOrderConfirmation; 